import { ReactTFC } from "../../../../utils/fp/missing-ts-parts";
import React from "react";

import { css } from "@emotion/react";

import { CSS_COLORS } from "../../../../styles/styles-css-variables";
import { CSS_MQ } from "../../../../styles/breakpoints";
import { fontColor, FONTS, fontSize } from "../../../../styles/fonts";
import cn from "classnames";
import { nonEmpty } from "@swo/shared";
import { PdpAvailabilityState } from "../availability/types";
import type { PDPStockLevelIndicator } from "./PDPStockLevelIndicator";
import { StockLevelIndicator } from "./StockLevelIndicator";
import { T } from "../../../../components/T";

export const PDPStickyPriceBar: ReactTFC<{
  primaryButton: React.ReactNode;
  extension: PdpAvailabilityState["extension"];
  priceLabel: string | undefined;
  stockLevel: PDPStockLevelIndicator;
}> = ({ primaryButton, priceLabel, extension, stockLevel }) => {
  const extensionChildren = extension
    ? [
        extension.tag === "component" && extension.Component ? (
          <React.Fragment key={"component"}>{extension.Component}</React.Fragment>
        ) : null,
        extension.text ? (
          <div key={"text"} css={textWrapperBigger} className={"_text"}>
            <p css={pureTextStyles}>
              <T>{extension.text}</T>
            </p>
          </div>
        ) : null,
      ].filter(nonEmpty)
    : [];

  return (
    <div
      className={cn("js-pdp-primary-action-above-the-fold-area", { "has-extension": extensionChildren.length > 0 })}
      css={css`
        display: grid;
        grid-template-columns: 1fr;
        grid-gap: 12px;

        ${CSS_MQ.atLeastXl} {
          grid-template-columns: minmax(0, 5fr) minmax(0, 4fr);
          grid-template-rows: auto auto;
        }
      `}
    >
      <div
        css={[
          css`
            display: flex;
            align-items: center;
            gap: 12px;

            color: ${CSS_COLORS.Green};
            align-self: end;
          `,
        ]}
      >
        <span css={[FONTS.SwarovskiFutura500, fontSize.x4, priceLabelStyles]}>{priceLabel}</span>

        <span
          css={css`
            margin-top: 2px;
          `}
        >
          <StockLevelIndicator stockLevel={stockLevel} />
        </span>
      </div>

      <span
        css={[
          desktopButtonsWrapper,
          css`
            ${CSS_MQ.atLeastXl} {
              grid-area: 2/1 / span 1 / span 1;
            }

            button,
            a {
              width: 100%;
              max-width: 20em;
            }
          `,
        ]}
      >
        {primaryButton}
      </span>

      {extensionChildren.length > 0 && (
        <div
          css={css`
            ${CSS_MQ.atLeastXl} {
              grid-area: 1/2 / span 2 / span 1;
            }

            display: flex;
            flex-direction: column;
            justify-content: flex-end;
          `}
        >
          {extensionChildren}
        </div>
      )}
    </div>
  );
};

const pureTextStyles = [
  FONTS.SwarovskiFutura300,
  fontSize.x2,
  fontColor.black,
  css`
    margin: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
];

const priceLabelStyles = css`
  align-self: center;

  justify-self: flex-start;
  ${CSS_MQ.atLeastMd} {
    justify-self: center;
  }
`;

const desktopButtonsWrapper = css`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  button,
  a {
    position: relative;
  }
`;

const textWrapperBigger = css`
  display: flex;
`;
